/**
 * Instructor space.
 *
 * Shows only what the API returns for this account: the modules and groups it is
 * assigned to. Corrections are fetched on demand from the guarded endpoint, and
 * every read is recorded in the audit trail server-side.
 */

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  BookOpenCheck,
  CheckCircle2,
  ClipboardCheck,
  Eye,
  FileCheck2,
  FlaskConical,
  ListChecks,
  ShieldCheck,
  Users,
  XCircle,
} from 'lucide-react';
import { Link, useParams } from 'react-router-dom';

import { MarkdownView } from '@/components/content/MarkdownView';
import { Alert, EmptyState } from '@/components/ui/Alert';
import { Badge, PublicationBadge } from '@/components/ui/Badge';
import { Button, ButtonLink } from '@/components/ui/Button';
import { Card, CardBody, CardHeader, CardTitle, StatTile } from '@/components/ui/Card';
import { FullPageSpinner } from '@/components/ui/Spinner';
import { formatPercent, relativeTime } from '@/lib/format';
import { api, ApiError } from '@/services/api';
import { fetchers, queryKeys } from '@/services/queries';
import type { LabSolution } from '@/types/api';

export function InstructorPage() {
  const [openGroup, setOpenGroup] = useState<string | null>(null);
  const [openModule, setOpenModule] = useState<string | null>(null);

  const modules = useQuery({
    queryKey: queryKeys.instructorModules,
    queryFn: fetchers.instructorModules,
  });
  const groups = useQuery({
    queryKey: queryKeys.instructorGroups,
    queryFn: fetchers.instructorGroups,
  });
  const candidates = useQuery({
    queryKey: queryKeys.groupCandidates(openGroup ?? ''),
    queryFn: () => fetchers.groupCandidates(openGroup as string),
    enabled: Boolean(openGroup),
  });
  const moduleContent = useQuery({
    queryKey: queryKeys.instructorModuleContent(openModule ?? ''),
    queryFn: () => fetchers.instructorModuleContent(openModule as string),
    enabled: Boolean(openModule),
  });

  if (modules.isLoading || groups.isLoading) {
    return <FullPageSpinner label="Chargement de l'espace formateur…" />;
  }

  const moduleList = modules.data ?? [];
  const groupList = groups.data ?? [];

  return (
    <div className="space-y-8">
      <header>
        <Badge tone="tech" className="mb-3">
          <BookOpenCheck className="h-3 w-3" />
          Espace formateur
        </Badge>
        <h1 className="text-2xl font-bold tracking-tight">Suivi pédagogique</h1>
        <p className="mt-1 text-sm text-muted">
          Vos modules affectés, vos groupes et les résultats de vos candidats.
        </p>
      </header>

      <section className="grid gap-4 sm:grid-cols-3">
        <StatTile
          label="Modules affectés"
          value={moduleList.length}
          icon={<BookOpenCheck className="h-4 w-4" />}
          accent="tech"
        />
        <StatTile
          label="Groupes suivis"
          value={groupList.length}
          icon={<Users className="h-4 w-4" />}
          accent="ai"
        />
        <StatTile
          label="Candidats"
          value={groupList.reduce((sum, group) => sum + group.member_count, 0)}
          icon={<Users className="h-4 w-4" />}
          accent="agent"
        />
      </section>

      {moduleList.length === 0 ? (
        <EmptyState
          icon={<BookOpenCheck className="mx-auto h-8 w-8" />}
          title="Aucun module affecté"
          description="Un administrateur doit vous affecter à un parcours, un module ou un groupe pour que le contenu apparaisse ici."
        />
      ) : (
        <div className="grid gap-6 lg:grid-cols-2">
          {/* Modules */}
          <Card>
            <CardHeader>
              <CardTitle as="h2">Mes modules</CardTitle>
            </CardHeader>
            <CardBody className="p-0">
              <ul className="divide-y divide-border">
                {moduleList.map((module) => (
                  <li key={module.id} className="px-5 py-3.5">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <div className="min-w-0">
                        <p className="truncate text-sm font-semibold text-fg">
                          <span className="font-mono text-xs text-tech">{module.code}</span>{' '}
                          {module.title}
                        </p>
                        <p className="mt-0.5 text-2xs text-faint">
                          {module.duration_hours} h · {module.level}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <PublicationBadge status={module.status} />
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() =>
                            setOpenModule((current) => (current === module.code ? null : module.code))
                          }
                        >
                          <Eye className="h-3.5 w-3.5" />
                          {openModule === module.code ? 'Masquer' : 'Contenu'}
                        </Button>
                      </div>
                    </div>

                    {openModule === module.code ? (
                      <div className="mt-4 rounded-lg border border-border bg-surface-2/40 p-4">
                        {moduleContent.isLoading ? (
                          <p className="text-xs text-muted">Chargement du contenu…</p>
                        ) : moduleContent.data ? (
                          <div className="space-y-4 text-xs">
                            <div>
                              <p className="font-semibold uppercase tracking-wide text-faint">
                                Chapitres ({moduleContent.data.chapters.length})
                              </p>
                              <ul className="mt-1.5 space-y-1">
                                {moduleContent.data.chapters.map((chapter) => (
                                  <li
                                    key={chapter.slug}
                                    className="flex items-center justify-between gap-2"
                                  >
                                    <Link
                                      to={`/app/modules/${module.code}/chapitres/${chapter.slug}`}
                                      className="min-w-0 flex-1 truncate text-muted hover:text-tech"
                                    >
                                      {chapter.title}
                                    </Link>
                                    {chapter.is_published ? (
                                      <CheckCircle2 className="h-3 w-3 shrink-0 text-ok" />
                                    ) : (
                                      <XCircle className="h-3 w-3 shrink-0 text-faint" />
                                    )}
                                  </li>
                                ))}
                              </ul>
                            </div>

                            <div>
                              <p className="font-semibold uppercase tracking-wide text-faint">
                                Travaux pratiques ({moduleContent.data.labs.length})
                              </p>
                              <ul className="mt-1.5 space-y-1.5">
                                {moduleContent.data.labs.map((lab) => (
                                  <li key={lab.id} className="flex items-center gap-2">
                                    <FlaskConical className="h-3 w-3 shrink-0 text-agent" />
                                    <span className="min-w-0 flex-1 truncate text-muted">
                                      {lab.title}
                                    </span>
                                    {lab.has_solution ? (
                                      <SolutionButton labId={lab.id} />
                                    ) : (
                                      <span className="shrink-0 text-2xs text-faint">
                                        sans correction
                                      </span>
                                    )}
                                  </li>
                                ))}
                              </ul>
                            </div>

                            <div>
                              <p className="font-semibold uppercase tracking-wide text-faint">
                                Évaluations ({moduleContent.data.quizzes.length})
                              </p>
                              <ul className="mt-1.5 space-y-1">
                                {moduleContent.data.quizzes.map((quiz) => (
                                  <li key={quiz.id} className="flex items-center gap-2 text-muted">
                                    <ListChecks className="h-3 w-3 shrink-0 text-ai" />
                                    <span className="min-w-0 flex-1 truncate">{quiz.title}</span>
                                    <span className="shrink-0 text-2xs text-faint">
                                      {quiz.questions} q · seuil {quiz.pass_score_percent} %
                                    </span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          </div>
                        ) : (
                          <Alert tone="danger">Contenu indisponible.</Alert>
                        )}
                      </div>
                    ) : null}
                  </li>
                ))}
              </ul>
            </CardBody>
          </Card>

          {/* Groups */}
          <Card>
            <CardHeader>
              <CardTitle as="h2" className="flex items-center gap-2">
                <Users className="h-4 w-4 text-ai" />
                Mes groupes
              </CardTitle>
            </CardHeader>
            <CardBody className="p-0">
              {groupList.length === 0 ? (
                <p className="px-5 py-6 text-sm text-muted">
                  Aucun groupe ne vous est affecté pour le moment.
                </p>
              ) : (
                <ul className="divide-y divide-border">
                  {groupList.map((group) => (
                    <li key={group.id} className="px-5 py-3.5">
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <div className="min-w-0">
                          <p className="truncate text-sm font-semibold text-fg">{group.name}</p>
                          <p className="text-2xs text-faint">
                            {group.member_count} candidat{group.member_count > 1 ? 's' : ''}
                          </p>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() =>
                            setOpenGroup((current) => (current === group.id ? null : group.id))
                          }
                        >
                          {openGroup === group.id ? 'Masquer' : 'Résultats'}
                        </Button>
                      </div>

                      {openGroup === group.id ? (
                        candidates.isLoading ? (
                          <p className="mt-3 text-xs text-muted">Chargement des résultats…</p>
                        ) : candidates.data && candidates.data.length > 0 ? (
                          <div className="mt-3 overflow-x-auto rounded-lg border border-border">
                            <table className="w-full text-xs">
                              <thead className="bg-surface-2">
                                <tr>
                                  <th className="px-3 py-2 text-left font-semibold">Candidat</th>
                                  <th className="px-3 py-2 text-right font-semibold">Chap.</th>
                                  <th className="px-3 py-2 text-right font-semibold">TP</th>
                                  <th className="px-3 py-2 text-right font-semibold">Quiz</th>
                                  <th className="px-3 py-2 text-right font-semibold">Moy.</th>
                                  <th className="px-3 py-2 text-right font-semibold">Prog.</th>
                                </tr>
                              </thead>
                              <tbody>
                                {candidates.data.map((candidate) => (
                                  <tr key={candidate.user_id} className="border-t border-border">
                                    <td className="px-3 py-2">
                                      <p className="font-medium text-fg">{candidate.full_name}</p>
                                      <p className="text-2xs text-faint">{candidate.email}</p>
                                    </td>
                                    <td className="px-3 py-2 text-right tabular-nums text-muted">
                                      {candidate.chapters_completed}
                                    </td>
                                    <td className="px-3 py-2 text-right tabular-nums text-muted">
                                      {candidate.labs_completed}
                                    </td>
                                    <td className="px-3 py-2 text-right tabular-nums text-muted">
                                      {candidate.quizzes_passed}
                                    </td>
                                    <td className="px-3 py-2 text-right tabular-nums text-muted">
                                      {formatPercent(candidate.average_score_percent)}
                                    </td>
                                    <td className="px-3 py-2 text-right font-semibold tabular-nums text-fg">
                                      {candidate.overall_percent} %
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        ) : (
                          <p className="mt-3 text-xs text-muted">
                            Aucun candidat dans ce groupe pour le moment.
                          </p>
                        )
                      ) : null}
                    </li>
                  ))}
                </ul>
              )}
            </CardBody>
          </Card>
        </div>
      )}

      <Alert tone="info" title="Protection des corrections">
        Les corrections et grilles d'évaluation sont servies par un endpoint distinct, réservé aux
        formateurs affectés au module et aux administrateurs. Chaque consultation est enregistrée
        dans le journal d'audit.
      </Alert>
    </div>
  );
}

/** Loads a correction on demand (one audited call, never prefetched). */
function SolutionButton({ labId }: { labId: string }) {
  const [open, setOpen] = useState(false);
  const solution = useQuery({
    queryKey: queryKeys.labSolution(labId),
    queryFn: () => api.get<LabSolution>(`/labs/${labId}/solution`),
    enabled: open,
    retry: false,
  });

  return (
    <>
      <Button variant="ghost" size="sm" className="shrink-0" onClick={() => setOpen((v) => !v)}>
        <ShieldCheck className="h-3 w-3" />
        {open ? 'Masquer' : 'Correction'}
      </Button>
      {open ? (
        <div className="mt-2 w-full basis-full rounded-lg border border-ai/30 bg-ai/[0.05] p-4">
          {solution.isLoading ? (
            <p className="text-xs text-muted">Chargement de la correction…</p>
          ) : solution.isError ? (
            <Alert tone="danger" title="Accès refusé">
              {solution.error instanceof ApiError
                ? solution.error.message
                : 'Correction indisponible.'}
            </Alert>
          ) : solution.data ? (
            <>
              <p className="flex items-center gap-2 text-2xs font-semibold uppercase tracking-wide text-ai">
                <ShieldCheck className="h-3 w-3" />
                Document formateur — accès journalisé
              </p>
              {solution.data.grading_grid.length > 0 ? (
                <table className="mt-3 w-full text-xs">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="py-1.5 text-left font-semibold">Critère</th>
                      <th className="py-1.5 text-right font-semibold">Points</th>
                    </tr>
                  </thead>
                  <tbody>
                    {solution.data.grading_grid.map((row, index) => (
                      <tr key={index} className="border-b border-border/50">
                        <td className="py-1.5 text-muted">{row.critere}</td>
                        <td className="py-1.5 text-right tabular-nums text-fg">{row.points}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : null}
              <div className="mt-4 max-h-96 overflow-y-auto rounded border border-border bg-surface p-4">
                <MarkdownView source={solution.data.solution_markdown} />
              </div>
            </>
          ) : null}
        </div>
      ) : null}
    </>
  );
}

/** Dedicated route for one correction, reached from the lab page. */
export function LabSolutionPage() {
  const { labId = '' } = useParams();
  const solution = useQuery({
    queryKey: queryKeys.labSolution(labId),
    queryFn: () => api.get<LabSolution>(`/labs/${labId}/solution`),
    retry: false,
  });

  if (solution.isLoading) return <FullPageSpinner label="Chargement de la correction…" />;

  if (solution.isError) {
    return (
      <div className="mx-auto max-w-2xl">
        <Alert tone="danger" title="Accès refusé">
          {solution.error instanceof ApiError
            ? solution.error.message
            : "Vous n'avez pas accès à cette correction."}
        </Alert>
        <ButtonLink to="/app/formateur" variant="outline" className="mt-6">
          Retour à l'espace formateur
        </ButtonLink>
      </div>
    );
  }

  const data = solution.data;
  if (!data) return null;

  return (
    <div className="mx-auto max-w-4xl space-y-6">
      <Link
        to="/app/formateur"
        className="inline-flex items-center gap-1.5 text-sm font-medium text-muted hover:text-fg"
      >
        ← Espace formateur
      </Link>

      <Alert tone="warning" title="Document réservé aux formateurs">
        Ce document ne doit pas être communiqué aux candidats. Votre consultation est enregistrée
        dans le journal d'audit de la plateforme.
      </Alert>

      <header>
        <Badge tone="ai">
          <ClipboardCheck className="h-3 w-3" />
          Correction formateur
        </Badge>
        <h1 className="mt-3 text-2xl font-bold tracking-tight">{data.title}</h1>
      </header>

      {data.grading_grid.length > 0 ? (
        <Card>
          <CardHeader>
            <CardTitle as="h2" className="flex items-center gap-2">
              <FileCheck2 className="h-4 w-4 text-ok" />
              Grille de correction
            </CardTitle>
          </CardHeader>
          <CardBody className="p-0">
            <table className="w-full text-sm">
              <thead className="bg-surface-2">
                <tr>
                  <th className="px-5 py-2.5 text-left font-semibold">Critère</th>
                  <th className="px-5 py-2.5 text-right font-semibold">Points</th>
                </tr>
              </thead>
              <tbody>
                {data.grading_grid.map((row, index) => (
                  <tr key={index} className="border-t border-border">
                    <td className="px-5 py-2.5 text-muted">{row.critere}</td>
                    <td className="px-5 py-2.5 text-right font-semibold tabular-nums text-fg">
                      {row.points}
                    </td>
                  </tr>
                ))}
                <tr className="border-t-2 border-border bg-surface-2/50">
                  <td className="px-5 py-2.5 font-bold">Total</td>
                  <td className="px-5 py-2.5 text-right font-bold tabular-nums">
                    {data.grading_grid.reduce((sum, row) => sum + (row.points ?? 0), 0)}
                  </td>
                </tr>
              </tbody>
            </table>
          </CardBody>
        </Card>
      ) : null}

      <Card>
        <CardBody>
          <MarkdownView source={data.solution_markdown} />
        </CardBody>
      </Card>

      <p className="text-2xs text-faint">Consulté {relativeTime(new Date().toISOString())}.</p>
    </div>
  );
}
